/**
 * Typed event catalog for the web SDK analytics.
 *
 * Thin wrappers over `emit()` that pin the three registered schemas (Viewed,
 * Clicked, Client Outcome) and their element-type enums, so event names and
 * shapes can't drift.
 */

import { emit, ANALYTICS_EVENT_NAMES } from "./analyticsClient";
import { OUTCOME_ELEMENT_BY_SECTION, type OutcomeSection } from "./documentCaptureEvents";

/** `elementType` values allowed by the Viewed schema. */
export type ViewedElementType = "section" | "card" | "banner" | "indicator" | "link" | "popup" | "widget";
/** `elementType` values allowed by the Clicked schema. */
export type ClickedElementType = "button" | "card" | "link" | "icon" | "tab" | "arrow" | "dot" | "widget";
/** Closed set of terminal results — see tracking-api's client-outcome/v1.json. */
export type Outcome = "passed" | "failed" | "abandoned" | "fraudulent";

export interface ViewedProps {
  section: string;
  /** Optional cause behind what appeared. Schema allows additionalProperties. */
  reason?: string;
}

export interface ClickedProps {
  section: string;
  targetAction?: "navigate" | "open" | "submit" | "toggle" | "expand" | "collapse";
}

export interface OutcomeProps {
  section: string;
  outcome: Outcome;
  /** Cause behind a non-passing outcome — each flow documents its own vocabulary. */
  reason?: string;
}

/** Emit a `Viewed` event (a screen/milestone that appeared — no result of its own). */
export function trackViewed(elementName: string, elementType: ViewedElementType, props: ViewedProps): void {
  emit(ANALYTICS_EVENT_NAMES.VIEWED, { elementType, elementName, ...props });
}

/** Emit a `Clicked` event (a control the user acted on — no result of its own). */
export function trackClicked(elementName: string, elementType: ClickedElementType, props: ClickedProps): void {
  emit(ANALYTICS_EVENT_NAMES.CLICKED, { elementType, elementName, ...props });
}

/**
 * Emit a `Client Outcome` — a flow concluded with a result.
 * `elementType` is always `"indicator"` (schema-narrowed), so callers don't pass it.
 *
 * One outcome per journey is each flow's own responsibility: route the terminal paths
 * through a single filer rather than emitting and de-duplicating.
 */
function trackOutcome(elementName: string, props: OutcomeProps): void {
  emit(ANALYTICS_EVENT_NAMES.CLIENT_OUTCOME, { elementType: "indicator", elementName, ...props });
}

/**
 * A flow's terminal success Outcome, filed against the `section` it concluded in.
 * `passed` means the flow reached a terminal, non-rejecting answer — for every flow, the
 * backend's verdict is read into this outcome. A rejection files `trackOutcomeFailed`
 * with a reason instead.
 */
export function trackOutcomePassed(section: OutcomeSection): void {
  trackOutcome(OUTCOME_ELEMENT_BY_SECTION[section], { section, outcome: "passed" });
}

/**
 * A flow's terminal failure Outcome. A `null` section emits nothing — that's how a flow
 * says "a Clicked already records this".
 */
export function trackOutcomeFailed(section: OutcomeSection | null, reason: string): void {
  if (!section) return;
  trackOutcome(OUTCOME_ELEMENT_BY_SECTION[section], { section, outcome: "failed", reason });
}
